/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CloudAccountCloudProvider } from "~/generated/graphql";
import { awsRegionsOptions, gcpRegionsOptions } from "~/constants";
import {
  FormDataAws,
  FormDataAzure,
  schemaAws,
  schemaAzure,
  FormDataGoogle,
  schemaGoogle,
} from "~/views/accounts/AccountCreate/components/formConfig";
import { ACCOUNT_CREDENTIALS_SECTION } from "../AccountDetailsForms/constants";

export function useAccountCreate() {
  const [provider, setProvider] = useState<CloudAccountCloudProvider | null>(
    null
  );

  const methodsAws = useForm<FormDataAws>({
    resolver: zodResolver(schemaAws),
    defaultValues: {
      regions: awsRegionsOptions,
    },
  });
  const {
    watch: watchAws,
    handleSubmit: handleSubmitAws,
    reset: resetAws,
  } = methodsAws;

  const {
    providerId,
    roleExternalId,
    roleArn,
    regions,
    displayName: awsDisplayName,
    description: awsDescription,
    environment: awsEnvironment,
  } = watchAws();

  const methodsAzure = useForm<FormDataAzure>({
    resolver: zodResolver(schemaAzure),
  });
  const {
    watch: watchAzure,
    handleSubmit: handleSubmitAzure,
    reset: resetAzure,
  } = methodsAzure;
  const {
    appId,
    subscriptionId,
    tenantId,
    displayName: azureDisplayName,
    description: azureDescription,
    environment: azureEnvironment,
  } = watchAzure();

  const methodsGoogle = useForm<FormDataGoogle>({
    resolver: zodResolver(schemaGoogle),
    defaultValues: {
      regions: gcpRegionsOptions,
    },
  });
  const {
    watch: watchGoogle,
    handleSubmit: handleSubmitGoogle,
    reset: resetGoogle,
  } = methodsGoogle;
  const {
    regions: googleRegions,
    displayName,
    description,
    environment,
    projectId,
    jsonFile,
  } = watchGoogle();

  const awsCredentials = useMemo(
    () => [
      {
        label: "Account Name",
        value: awsDisplayName || "Not specified",
      },
      {
        label: "Description",
        value: awsDescription || "Not specified",
      },
      {
        label: "Environment",
        value: awsEnvironment || "Not specified",
      },
      {
        label: "Regions",
        value: regions.map(({ value }) => value).join(", "),
      },
      {
        label: "Account ID",
        value: providerId,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
      {
        label: "Role External ID",
        value: roleExternalId,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
      {
        label: "Role ARN",
        value: roleArn,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
    ],
    [
      awsDisplayName,
      awsDescription,
      awsEnvironment,
      regions,
      providerId,
      roleExternalId,
      roleArn,
    ]
  );

  const azureCredentials = useMemo(
    () => [
      {
        label: "Account Name",
        value: azureDisplayName || "Not specified",
      },
      {
        label: "Description",
        value: azureDescription || "Not specified",
      },
      {
        label: "Environment",
        value: azureEnvironment || "Not specified",
      },
      {
        label: "Subscription ID",
        value: subscriptionId,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
      {
        label: "App ID",
        value: appId,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
      {
        label: "Tenant",
        value: tenantId,
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
    ],
    [
      azureDisplayName,
      azureDescription,
      azureEnvironment,
      subscriptionId,
      appId,
      tenantId,
    ]
  );

  const googleCredentials = useMemo(
    () => [
      {
        label: "Account Name",
        value: displayName || "Not specified",
      },
      {
        label: "Description",
        value: description || "Not specified",
      },
      {
        label: "Environment",
        value: environment || "Not specified",
      },
      {
        label: "Regions",
        value:
          googleRegions?.map(({ value }) => value).join(", ") ||
          "No regions selected",
      },
      {
        label: "Project ID",
        value: projectId || "Not specified",
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
      {
        label: "Key",
        value: jsonFile?.name || "No file uploaded",
        section: ACCOUNT_CREDENTIALS_SECTION,
      },
    ],
    [displayName, description, environment, googleRegions, projectId, jsonFile]
  );

  const resetForms = () => {
    resetAws();
    resetAzure();
    resetGoogle();
  };

  const accountCredentials: {
    label: string;
    value: string;
    section?: string;
  }[] = (() => {
    switch (provider) {
      case CloudAccountCloudProvider.Aws:
        return awsCredentials;
      case CloudAccountCloudProvider.Azure:
        return azureCredentials;
      case CloudAccountCloudProvider.Gc:
        return googleCredentials;
      default:
        return [];
    }
  })();

  return {
    accountCredentials,
    resetForms,
    handleSubmitAws,
    handleSubmitAzure,
    handleSubmitGoogle,
    provider,
    setProvider,
    methodsAws,
    methodsAzure,
    methodsGoogle,
  };
}
